import React from 'react';
import type { CollapseProps } from 'antd';
import { Dayjs } from 'dayjs';
import { Collapse, ConfigProvider } from 'antd';
import { Form, useFormikContext } from 'formik';
import {
  AphDatePicker,
  AphSelect,
  useFetchServiceV3
} from '@aph/common-react';

import { defaultPageSize } from 'global.constants';
import { updateUrlSearchParameters } from 'global.helpers';
import { setupFilteredTagsList } from 'helpers/tag.helpers';
import { IStatisticsSearchFormData } from 'types/form.types';
import {
  handleRemoveSelectedSearchFilterTag,
  setupDateForRequest
} from 'helpers/form.helpers';
import {
  IStatisticsResult,
  IStatisticsSearchRequest,
  IUseSearchService,
  IGetLookupResponse
} from 'services';

import {
  NoResultsFound,
  SearchBar,
  SearchFilteredTags,
  SearchResultsWrapper,
  StatisticsSearchResultItem
} from 'components';

import './StatisticsSearchInner.scss';

/**
 * IStatisticsSearchInner
 *
 * @description Component properties for the Statistics search component
 */
interface IStatisticsSearchInner {
  searchService: IUseSearchService;
  currentSearchRequest: IStatisticsSearchRequest;
  searchResults: {
    loading: boolean;
    results: Array<IStatisticsResult>;
    currentPage: number;
    pageSize: number;
    rowCount: number;
  };
  filterSearchResults: (request: IStatisticsSearchRequest) => Promise<void>;
  paginationHandler: (selectedPage: number) => Promise<void>;
}

/**
 * StatisticsSearchInner
 *
 * @description
 */
const StatisticsSearchInner: React.FC<IStatisticsSearchInner> = ({
  searchService,
  searchResults,
  currentSearchRequest,
  filterSearchResults,
  paginationHandler
}) => {
  const { items: categories } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getStatisticsSearchCategories, { loadOnInitiate: true });
  const { items: types } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getStatisticsSearchTypes, { loadOnInitiate: true });

  const { values: formValues, setFieldValue } = useFormikContext<IStatisticsSearchFormData>();

  /**
   * HandleFormFieldOnChange
   *
   * @description On change handler for filters
   * @param {string} key   Form field name
   * @param {string | Array<string>} value Field value
   * @returns {void}
   */
  const handleFormFieldOnChange = (key: string, value: string | Array<string>): void => {
    updateUrlSearchParameters(key, value);

    const updatedSearchRequest: IStatisticsSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      [key]: value
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateFromFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateFromFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;

    updateUrlSearchParameters('dateFrom', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IStatisticsSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateFrom: dataRequestSetup,
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateToFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateToFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;

    updateUrlSearchParameters('dateTo', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: IStatisticsSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateTo: dataRequestSetup,
    };

    filterSearchResults(updatedSearchRequest);
  };

  // This defines the content for the collapse
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Filters',
      children:
        <div className='library-publication-search__filters-inner'>
          <div className='library-publication-search__filter-column'>
            <h3>Topics</h3>
            <AphSelect
              name='topics'
              mode='multiple'
              placeholder='Start typing to search...'
              options={categories ?? []}
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('topics', value)}
            />
          </div>
          <div className='library-publication-search__filter-column'>
            <h3>Type</h3>
            <AphSelect
              name='type'
              placeholder='Select type'
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('type', value)}
              options={types ?? []}
            />

          </div>
          <div className='library-publication-search__filter-column'>
            <h3>When</h3>
            <div className='library-publication-search__filter-date'>
              <AphDatePicker
                name='dateFrom'
                placeholder='Start date'
                onChange={(value: Dayjs | null) => handleDateFromFieldOnChange(value)}
              />
              <AphDatePicker
                name='dateTo'
                placeholder='End date'
                onChange={(value: Dayjs | null) => handleDateToFieldOnChange(value)}
              />
            </div>
          </div>
        </div>
    }
  ];

  return (
    <div className='library-publication-search'>
      <Form>
        <div className='library-publication-search__filters'>
          <SearchBar
            theme='statistics'
            name='searchTerms'
            showClear
          />
          <ConfigProvider
            theme={{
              token: {
                borderRadiusLG: 0,
                lineWidth: 0
              }
            }}
          >
            <Collapse
              className='library-publication-search__filters--additional'
              defaultActiveKey={['1']}
              items={items}
            />
          </ConfigProvider>
        </div>
        <SearchResultsWrapper
          loading={searchResults.loading && (!searchResults.results || searchResults.results.length <= 0)}
          paginationConfig={{
            currentPage: searchResults.currentPage ?? 1,
            totalResults: searchResults.rowCount ?? 0,
            pageSize: searchResults.pageSize ?? defaultPageSize,
            onPaginate: paginationHandler
          }}
        >
          <SearchFilteredTags
            tags={setupFilteredTagsList('topics', formValues.topics, categories)}
            onRemove={(id, fieldName) =>
              handleRemoveSelectedSearchFilterTag(fieldName, id, formValues.topics, setFieldValue, handleFormFieldOnChange)}
          />
          {
            searchResults.results &&
            searchResults.results.length > 0
              ?
              <>
                {
                  searchResults.results.map((searchResult, index) =>
                    <StatisticsSearchResultItem
                      key={index}
                      result={{
                        ...searchResult
                      }}
                    />
                  )
                }
              </>
              : <NoResultsFound />
          }
        </SearchResultsWrapper>
      </Form>
    </div>
  );
};

export default StatisticsSearchInner;
